/**
 * Pricing Utility Functions
 *
 * Helper functions for pricing option calculations and display labels.
 */

import { PricingOptionType, RentalPricingTier, SubscriptionDuration } from '@/types/cart';
import type { CustomerReservationItemResponse } from '@/types/reservations';

type ShortTermPricingField =
    | 'price1Day'
    | 'price2To3Days'
    | 'price4To7Days'
    | 'price8To14Days'
    | 'price15To21Days'
    | 'price22To31Days';

const SHORT_TERM_PRICE_FIELDS: Array<{ maxDays: number; field: ShortTermPricingField }> = [
    { maxDays: 1, field: 'price1Day' },
    { maxDays: 3, field: 'price2To3Days' },
    { maxDays: 7, field: 'price4To7Days' },
    { maxDays: 14, field: 'price8To14Days' },
    { maxDays: 21, field: 'price15To21Days' },
    { maxDays: 31, field: 'price22To31Days' },
];

type ExtensionPricePreview = {
    currentTotalPrice: number;
    newTotalPrice: number;
    linePriceDelta: number;
    currentLabel: string;
    nextLabel: string;
    isProrated: boolean;
};

function roundCurrency(amount: number): number {
    return Math.round((amount + Number.EPSILON) * 100) / 100;
}

function hasPublishedTierPrices(item: CustomerReservationItemResponse): boolean {
    return [
        item.price1Day,
        item.price2To3Days,
        item.price4To7Days,
        item.price8To14Days,
        item.price15To21Days,
        item.price22To31Days,
    ].some((price) => price != null);
}

function getFallbackTotalPrice(item: CustomerReservationItemResponse, days: number, currentDays: number): number | null {
    if (days <= 0 || days > 90) {
        return null;
    }

    if (currentDays > 0 && item.totalPrice > 0 && days === currentDays) {
        return roundCurrency(item.totalPrice);
    }

    if (item.unitPrice <= 0) {
        return null;
    }

    return roundCurrency(item.unitPrice * days * item.quantity);
}

function getShortTermTierPrice(item: CustomerReservationItemResponse, days: number): number | null {
    if (days <= 0) {
        return null;
    }

    const matchingField = SHORT_TERM_PRICE_FIELDS.find((tier) => days <= tier.maxDays)?.field;
    if (!matchingField) {
        return null;
    }

    const price = item[matchingField];
    return price != null ? price : null;
}

function getShortTermTotalPrice(item: CustomerReservationItemResponse, days: number): number | null {
    if (days <= 0 || days > 90) {
        return null;
    }

    if (days <= 31) {
        const tierPrice = getShortTermTierPrice(item, days);
        return tierPrice == null ? null : roundCurrency(tierPrice);
    }

    if (item.price22To31Days == null) {
        return null;
    }

    return roundCurrency((item.price22To31Days / 31) * days);
}

export function getExtensionPricePreview(
    item: CustomerReservationItemResponse,
    currentDays: number,
    newDays: number
): ExtensionPricePreview | null {
    const publishedCurrentTotalPrice = hasPublishedTierPrices(item)
        ? getShortTermTotalPrice(item, currentDays)
        : null;
    const publishedNewTotalPrice = hasPublishedTierPrices(item)
        ? getShortTermTotalPrice(item, newDays)
        : null;
    const currentTotalPrice = publishedCurrentTotalPrice ?? getFallbackTotalPrice(item, currentDays, currentDays);
    const newTotalPrice = publishedNewTotalPrice ?? getFallbackTotalPrice(item, newDays, currentDays);

    if (currentTotalPrice == null || newTotalPrice == null) {
        return null;
    }

    const isProrated = newDays > 31;
    const isFallbackPreview = publishedCurrentTotalPrice == null || publishedNewTotalPrice == null;
    const linePriceDelta = roundCurrency(
        Math.max(0, newTotalPrice - currentTotalPrice) * (isFallbackPreview ? 1 : item.quantity)
    );

    return {
        currentTotalPrice,
        newTotalPrice,
        linePriceDelta,
        currentLabel: getRentalTierDisplayLabel(getRentalTierFromDays(Math.min(currentDays, 31))),
        nextLabel: isProrated
            ? 'Prorated beyond 31 days'
            : getRentalTierDisplayLabel(getRentalTierFromDays(newDays)),
        isProrated,
    };
}

/**
 * Normalize a rental tier value from any source (local numeric enum, API string
 * enum, or stale persisted cart) into the local numeric enum.
 *
 * The API contract auto-generates `RentalPricingTier` as a string enum
 * (`"Days8To14"`), while the in-app enum uses numbers (`Days8To14 = 4`). Without
 * this conversion, `getTierDayRange("Days8To14")` falls through every numeric
 * `case` and returns the default fallback — which is exactly what manifested
 * as "I can pick any date in the next month" in checkout.
 */
export function normalizeRentalPricingTier(
    tier: RentalPricingTier | string | number | null | undefined | unknown
): RentalPricingTier | null {
    if (tier == null) return null;
    if (typeof tier === 'number') {
        return tier in RentalPricingTier ? (tier as RentalPricingTier) : null;
    }
    switch (tier) {
        case 'Default': return RentalPricingTier.Default;
        case 'Day1': return RentalPricingTier.Day1;
        case 'Days2To3': return RentalPricingTier.Days2To3;
        case 'Days4To7': return RentalPricingTier.Days4To7;
        case 'Days8To14': return RentalPricingTier.Days8To14;
        case 'Days15To21': return RentalPricingTier.Days15To21;
        // The local enum still uses the legacy `Days22To40` name; the backend
        // serializes the same tier as either `Days22To40` or `Days22To31`.
        case 'Days22To40':
        case 'Days22To31':
            return RentalPricingTier.Days22To40;
        default: return null;
    }
}

/**
 * Get min/max day bounds for a rental pricing tier.
 *
 * Accepts either the numeric local enum or the string form returned by the API
 * — both are normalized internally.
 */
export function getTierDayRange(
    tier: RentalPricingTier | string | null | undefined
): { minDays: number; maxDays: number } {
    switch (normalizeRentalPricingTier(tier)) {
        case RentalPricingTier.Day1:
            return { minDays: 1, maxDays: 1 };
        case RentalPricingTier.Days2To3:
            return { minDays: 2, maxDays: 3 };
        case RentalPricingTier.Days4To7:
            return { minDays: 4, maxDays: 7 };
        case RentalPricingTier.Days8To14:
            return { minDays: 8, maxDays: 14 };
        case RentalPricingTier.Days15To21:
            return { minDays: 15, maxDays: 21 };
        case RentalPricingTier.Days22To40:
            // Enum name preserved for backwards compatibility, but the actual
            // tier and price field (price22To31Days) cap at 31 days. Backend
            // RentalTierHelper.GetTierBoundsForTier agrees: (22, 31).
            return { minDays: 22, maxDays: 31 };
        default:
            return { minDays: 1, maxDays: 31 };
    }
}

/**
 * Calculate the rental tier based on number of days.
 */
export function getRentalTierFromDays(days: number): RentalPricingTier {
    if (days <= 1) return RentalPricingTier.Day1;
    if (days <= 3) return RentalPricingTier.Days2To3;
    if (days <= 7) return RentalPricingTier.Days4To7;
    if (days <= 14) return RentalPricingTier.Days8To14;
    if (days <= 21) return RentalPricingTier.Days15To21;
    return RentalPricingTier.Days22To40;
}

/**
 * Get display label for a pricing option.
 */
export function getPricingOptionLabel(
    type: PricingOptionType,
    tier?: RentalPricingTier,
    months?: number | SubscriptionDuration
): string {
    switch (type) {
        case PricingOptionType.MonthlySubscription:
            if (months === SubscriptionDuration.MonthToMonth || months === 0 || months === 1) {
                return 'Flexible (Cancel Anytime)';
            }
            return `${months ?? 3}-Month Subscription`;

        case PricingOptionType.Rental:
            if (tier !== undefined && tier !== RentalPricingTier.Default) {
                return getRentalTierDisplayLabel(tier);
            }
            return 'Short-Term Rental';

        case PricingOptionType.RentToBuy:
            return 'Rent to Buy';

        case PricingOptionType.Buy:
            return 'Purchase';

        default:
            return 'Rental';
    }
}

/**
 * Get display label for a rental tier.
 *
 * Accepts either the numeric local enum or the string form returned by the API.
 */
export function getRentalTierDisplayLabel(tier: RentalPricingTier | string): string {
    switch (normalizeRentalPricingTier(tier)) {
        case RentalPricingTier.Day1:
            return '1-Day Rental';
        case RentalPricingTier.Days2To3:
            return '2-3 Day Rental';
        case RentalPricingTier.Days4To7:
            return 'Weekly Rental';
        case RentalPricingTier.Days8To14:
            return '2-Week Rental';
        case RentalPricingTier.Days15To21:
            return '3-Week Rental';
        case RentalPricingTier.Days22To40:
            return '22-40 Day Rental';
        default:
            return 'Short-Term Rental';
    }
}

/**
 * Validate a subscription months value.
 * 0 = month-to-month (no commitment)
 * 3, 6, 12 = fixed commitment periods
 */
export function isValidSubscriptionMonths(months: number): boolean {
    return [0, 3, 6, 12].includes(months);
}

/**
 * Check if a subscription has no minimum commitment.
 */
export function isNoCommitmentSubscription(months: number | SubscriptionDuration): boolean {
    return months === SubscriptionDuration.MonthToMonth || months === 0;
}

/**
 * Get the subscription label for display
 */
export function getSubscriptionDisplayLabel(months: number | SubscriptionDuration): string {
    switch (months) {
        case SubscriptionDuration.MonthToMonth:
        case 0:
            return 'Flexible (Monthly)';
        case SubscriptionDuration.Months3:
        case 3:
            return '3-Month Plan';
        case SubscriptionDuration.Months6:
        case 6:
            return '6-Month Plan';
        case SubscriptionDuration.Months12:
        case 12:
            return '12-Month Plan';
        default:
            return `${months}-Month Plan`;
    }
}

/**
 * Default pricing option type for new cart items
 */
export const DEFAULT_PRICING_OPTION = PricingOptionType.Rental;

/**
 * Default subscription duration when subscription is selected
 */
export const DEFAULT_SUBSCRIPTION_MONTHS = SubscriptionDuration.MonthToMonth;
